import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
const hasRequiredFirebaseConfig = requiredKeys.every((key) => {
  const value = firebaseConfig[key];
  return typeof value === 'string' && value.trim().length > 0;
});

let auth      = null;
let db        = null;
let messaging = null;
let isFirebaseConfigured = hasRequiredFirebaseConfig;

if (!hasRequiredFirebaseConfig) {
  isFirebaseConfigured = false;
  console.warn('[Auth] Firebase config is missing. Falling back to local auth mode.');
} else {
  try {
    const app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db   = getFirestore(app);
    // Messaging is only available in secure browser contexts (https or localhost)
    if ('serviceWorker' in navigator) {
      try { messaging = getMessaging(app); } catch { messaging = null; }
    }
  } catch (error) {
    isFirebaseConfigured = false;
    auth = null;
    db   = null;
    console.warn('[Auth] Firebase init failed. Falling back to local auth mode.', error);
  }
}

/**
 * Request notification permission and return the FCM token.
 * Requires REACT_APP_FIREBASE_VAPID_KEY in .env.
 */
export async function requestFcmToken() {
  if (!messaging) return null;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    return await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js'),
    });
  } catch {
    return null;
  }
}

/** Call this once after login to show foreground push notifications as browser alerts. */
export function listenForegroundMessages() {
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    const { title = 'SublingoX', body = '' } = payload.notification ?? {};
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/logo192.png' });
    }
  });
}

export { auth, db, messaging, isFirebaseConfigured };
